import type { FunctionComponent, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import type { FallbackProps } from 'react-error-boundary';
import { appInsights } from '../AppInsights';
import Status, { StatusValue } from '../Data/Status';
import { toError } from '../Utilities/toError';
import { ErrorPage } from './ErrorPage';

/**
 * Adapts react-error-boundary's FallbackProps to ErrorPage, which is driven by the survey's
 * Status codes. A render error has no status of its own, so the generic UNKNOWN copy is shown.
 */
export const ErrorBoundaryFallback: FunctionComponent<FallbackProps> = ({ resetErrorBoundary }) => {
  const setStatus = (status: StatusValue) => {
    // ErrorPage only ever asks for SUCCESS (to recover); re-mount the crashed tree in that case.
    if (status === Status.SUCCESS) {
      resetErrorBoundary();
    }
  };

  return <ErrorPage status={Status.UNKNOWN} setStatus={setStatus} />;
};

interface AppErrorBoundaryProps {
  children: ReactNode;
}

/**
 * Last line of defence for uncaught render errors anywhere in the survey. Errors caught by a
 * boundary never reach window.onerror, so Application Insights' auto-collection misses them;
 * they are reported explicitly here together with the React component stack.
 */
export const AppErrorBoundary: FunctionComponent<AppErrorBoundaryProps> = ({ children }) => (
  <ErrorBoundary
    FallbackComponent={ErrorBoundaryFallback}
    onError={(thrown, info) =>
      appInsights.trackException({
        exception: toError(thrown),
        properties: { componentStack: info.componentStack }
      })
    }
  >
    {children}
  </ErrorBoundary>
);
